var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { computed, observable, runInAction } from "mobx";
import { getObservableT } from "../Infra/i18next";
import { NamedRoutes } from "./NamedRoutes";
const t = getObservableT("patients");
export class AccountLinkingConflictViewModel {
    constructor(container) {
        this.selectedPatientId = null;
        this.isLoading = false;
        this.error = null;
        this.authManager = container.authManager;
        this.authService = container.authService;
        this.persistence = container.persistence;
        this.routingUIManager = container.routingUIManager;
        this.analyticsService = container.analyticsService;
        this.clearAuthManager = container.clearAuthManager;
        this.patientConsentUIManager = container.patientConsentUIManager;
        this.trackConflictShown();
    }
    get conflictData() {
        var _a;
        return (_a = this.authManager.accountLinkingConflict) !== null && _a !== void 0 ? _a : this.persistence.getConflictResolutionData();
    }
    get existingAccount() {
        var _a, _b;
        return (_b = (_a = this.conflictData) === null || _a === void 0 ? void 0 : _a.existingAccount) !== null && _b !== void 0 ? _b : null;
    }
    get clearAccount() {
        var _a, _b;
        return (_b = (_a = this.conflictData) === null || _a === void 0 ? void 0 : _a.clearAccount) !== null && _b !== void 0 ? _b : null;
    }
    get title() {
        return t("We found two accounts");
    }
    get subtitle() {
        return t("The email you used for identity verification is linked to a different HealthEx account. Which account would you like to continue with?");
    }
    get continueButtonLabel() {
        return t("Continue with selected account");
    }
    get needHelpPrefix() {
        return t("Need help?");
    }
    get contactSupportText() {
        return t("Contact support");
    }
    get supportSubtext() {
        return t("Our team can walk you through resolving your two accounts.");
    }
    getAccountDescription(hasClearAuth) {
        return hasClearAuth ? t("We'll automatically find your records") : t("Add providers manually to find records");
    }
    get canContinue() {
        return this.selectedPatientId !== null && !this.isLoading;
    }
    formatLastActive(date) {
        if (!date)
            return "";
        const now = new Date();
        const dateObj = new Date(date);
        const diffDays = Math.floor((now.getTime() - dateObj.getTime()) / (1000 * 60 * 60 * 24));
        let formatted;
        if (diffDays < 7) {
            formatted = dateObj.toLocaleDateString("en-US", { weekday: "long" });
        }
        else {
            formatted = dateObj.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
        }
        return t("Last active {{date}}", { date: formatted });
    }
    selectAccount(patientId) {
        this.selectedPatientId = patientId;
    }
    get verifiedAccount() {
        var _a, _b;
        if ((_a = this.clearAccount) === null || _a === void 0 ? void 0 : _a.hasClearAuth)
            return this.clearAccount;
        if ((_b = this.existingAccount) === null || _b === void 0 ? void 0 : _b.hasClearAuth)
            return this.existingAccount;
        return null;
    }
    get nonVerifiedAccount() {
        var _a, _b;
        if ((_a = this.clearAccount) === null || _a === void 0 ? void 0 : _a.hasClearAuth)
            return this.existingAccount;
        if ((_b = this.existingAccount) === null || _b === void 0 ? void 0 : _b.hasClearAuth)
            return this.clearAccount;
        return this.clearAccount;
    }
    initializeDefaultSelection() {
        if (this.selectedPatientId === null && this.verifiedAccount) {
            this.selectedPatientId = this.verifiedAccount.patientId;
        }
    }
    trackConflictShown() {
        var _a, _b;
        try {
            heap.track("AccountLinkingConflictShown", {
                existingPatientId: (_a = this.existingAccount) === null || _a === void 0 ? void 0 : _a.patientId,
                newPatientId: (_b = this.clearAccount) === null || _b === void 0 ? void 0 : _b.patientId,
            });
        }
        catch (e) {
            console.log("heap exception", e);
        }
    }
    trackConflictResolved(selectedPatientId) {
        var _a, _b;
        try {
            heap.track("AccountLinkingConflictResolved", {
                selectedPatientId,
                existingPatientId: (_a = this.existingAccount) === null || _a === void 0 ? void 0 : _a.patientId,
                newPatientId: (_b = this.clearAccount) === null || _b === void 0 ? void 0 : _b.patientId,
            });
        }
        catch (e) {
            console.log("heap exception", e);
        }
    }
    async resolveConflict() {
        var _a;
        if (!this.selectedPatientId || !this.conflictData) {
            return;
        }
        const selectedClearAccount = ((_a = this.clearAccount) === null || _a === void 0 ? void 0 : _a.patientId) === this.selectedPatientId;
        runInAction(() => {
            this.isLoading = true;
            this.error = null;
        });
        try {
            if (selectedClearAccount) {
                const result = await this.authService.switchAccount({ accountSwitchToken: this.conflictData.accountSwitchToken }, this.authManager);
                await this.authManager.handleConflictResolutionResult(result.token, result.expiration, result.user);
            }
            this.trackConflictResolved(this.selectedPatientId);
            this.authManager.clearAccountLinkingConflict();
            const clearAuthData = this.clearAuthManager.getClearAuthData();
            if (clearAuthData === null || clearAuthData === void 0 ? void 0 : clearAuthData.nextPageRoute) {
                this.patientConsentUIManager.goToPage(clearAuthData.nextPageRoute);
                if (!selectedClearAccount) {
                    this.clearAuthManager.resetAuth();
                }
            }
            else if (clearAuthData === null || clearAuthData === void 0 ? void 0 : clearAuthData.callbackHash) {
                this.clearAuthManager.resetAuth();
                this.patientConsentUIManager.goToPage(clearAuthData.callbackHash);
            }
            else {
                this.routingUIManager.navigateTo(NamedRoutes.PATIENT_HOME);
            }
        }
        catch (e) {
            console.error("Failed to resolve conflict", e);
            runInAction(() => {
                this.error = t("Failed to resolve conflict. Please try again or contact support.");
            });
        }
        finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }
}
__decorate([
    observable
], AccountLinkingConflictViewModel.prototype, "selectedPatientId", void 0);
__decorate([
    observable
], AccountLinkingConflictViewModel.prototype, "isLoading", void 0);
__decorate([
    observable
], AccountLinkingConflictViewModel.prototype, "error", void 0);
__decorate([
    computed
], AccountLinkingConflictViewModel.prototype, "conflictData", null);
__decorate([
    computed
], AccountLinkingConflictViewModel.prototype, "existingAccount", null);
__decorate([
    computed
], AccountLinkingConflictViewModel.prototype, "clearAccount", null);
__decorate([
    computed
], AccountLinkingConflictViewModel.prototype, "title", null);
__decorate([
    computed
], AccountLinkingConflictViewModel.prototype, "subtitle", null);
__decorate([
    computed
], AccountLinkingConflictViewModel.prototype, "continueButtonLabel", null);
__decorate([
    computed
], AccountLinkingConflictViewModel.prototype, "needHelpPrefix", null);
__decorate([
    computed
], AccountLinkingConflictViewModel.prototype, "contactSupportText", null);
__decorate([
    computed
], AccountLinkingConflictViewModel.prototype, "supportSubtext", null);
__decorate([
    computed
], AccountLinkingConflictViewModel.prototype, "canContinue", null);
__decorate([
    computed
], AccountLinkingConflictViewModel.prototype, "verifiedAccount", null);
__decorate([
    computed
], AccountLinkingConflictViewModel.prototype, "nonVerifiedAccount", null);
